<template>
  <CoreDrawer
    :value="value"
    :title="placeFilterLabel"
    :width="376"
    is-back
    data-qa="analyses_lpu_cards_place_drawer"
    @input="emit('input', false)"
    @back="emit('back')"
  >
    <template #header>
      <CoreSearchField
        v-if="isVisibleSearch"
        v-model.trim="searchQuery"
        autofocus
        maxlength="150"
        class="mt-2"
        :placeholder="searchPlaceholder"
        @click:clear="handleClickClear"
      />
    </template>

    <template #append-header>
      <v-btn
        v-if="!isDefaultPlace"
        color="primary"
        small
        text
        class="ml-auto"
        data-qa="analyses_lpu_cards_place_reset_button"
        @click="resetPlace"
      >
        Сбросить
      </v-btn>
    </template>

    <DrawerError
      v-if="isError"
      class="py-9"
    />
    <DrawerSkeleton
      v-else-if="isLoading"
      class="px-2"
    />
    <CoreSelectionList
      v-else
      ref="selectionListRef"
      class="py-2"
      list-data-qa="analyses_lpu_cards_place_list"
      list-item-data-qa="analyses_lpu_cards_place_list_item"
      :items="places"
      :value="draftPlace || defaultPlace"
      :search-input="searchQuery"
      end-icon="check"
      @input="onPlaceClick"
    >
      <template #empty>
        <DrawerNotFound class="py-3" />
      </template>
    </CoreSelectionList>

    <template #footer>
      <div class="analyses-lpu-place-drawer__footer pa-2 elevation-8">
        <v-btn
          block
          depressed
          color="primary"
          data-qa="analyses_lpu_cards_place_apply_button"
          :disabled="!hasUnsavedPlace"
          @click="applyPlace"
        >
          Применить
        </v-btn>
      </div>
    </template>
  </CoreDrawer>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue'
import CoreDrawer from 'src/core/CoreDrawer/CoreDrawer.vue'
import CoreSearchField from 'src/core/CoreSearchField/CoreSearchField.vue'
import CoreSelectionList from 'src/core/CoreSelectionList/CoreSelectionList.vue'
import type { CoreSelectionListSelectedItem } from 'src/core/CoreSelectionList/types'
import DrawerError from 'src/core/CoreSelectionDrawer/components/DrawerError.vue'
import DrawerSkeleton from 'src/core/CoreSelectionDrawer/components/DrawerSkeleton.vue'
import DrawerNotFound from 'src/core/CoreSelectionDrawer/components/DrawerNotFound.vue'
import { DEFAULT_ANALYSES_PLACE } from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-place'
import {
  getAnalysesPlaceFilterLabel,
  getAnalysesPlaceSearchPlaceholder,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/get-analyses-place-filter-label'
import apiAnalysesDistricts from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesDistricts'
import type { AnalysesPlaceItem } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesDistricts.types'
import type { AnalysesPageContext } from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'

const props = defineProps<{
  value: boolean
  townId?: number
  selectedPlace?: AnalysesPlaceItem
}>()

type Emits = {
  (_event: 'input', _value: boolean): void
  (_event: 'back'): void
  (_event: 'apply', _place?: AnalysesPlaceItem): void
  (_event: 'request-error', _error: unknown): void
}

const emit = defineEmits<Emits>()

const { context } = useContextStore() as CommonContextStore & {
  context: AnalysesPageContext
}

const selectionListRef = ref<InstanceType<typeof CoreSelectionList>>()
const searchQuery = ref('')
const places = ref<AnalysesPlaceItem[]>([])
const draftPlace = ref<AnalysesPlaceItem | undefined>()
const isLoading = ref(false)
const isError = ref(false)

const defaultPlace = DEFAULT_ANALYSES_PLACE
const hasMetro = computed(() => Boolean(context.town.has_metro))
const placeFilterLabel = computed(() => getAnalysesPlaceFilterLabel(hasMetro.value))
const searchPlaceholder = computed(() => getAnalysesPlaceSearchPlaceholder(hasMetro.value))
const isVisibleSearch = computed(() => !isLoading.value && !isError.value)
const isDefaultPlace = computed(() => !draftPlace.value)
const hasUnsavedPlace = computed(() => {
  const appliedTransliteration = props.selectedPlace?.translit
  const draftTransliteration = draftPlace.value?.translit

  return draftTransliteration !== appliedTransliteration
})

watch(() => props.value, async isOpen => {
  resetSearch()

  if (!isOpen) {
    return
  }

  draftPlace.value = props.selectedPlace

  const currentTownId = props.townId

  if (!currentTownId) {
    isError.value = true
    return
  }

  await loadPlaces(currentTownId)
  await nextTick()
  scrollToActiveItem()
})

watch(() => props.selectedPlace, place => {
  if (!props.value) {
    draftPlace.value = place
  }
})

async function loadPlaces(id: number) {
  isError.value = false
  isLoading.value = true

  try {
    const { data } = await apiAnalysesDistricts.get({ town_id: id })

    places.value = [
      defaultPlace,
      ...data.metro.map(item => ({ ...item, isMetro: true })),
      ...data.district.map(item => ({ ...item, isMetro: false })),
    ]
  } catch (error) {
    isError.value = true
    emit('request-error', error)
  } finally {
    isLoading.value = false
  }
}

function scrollToActiveItem() {
  selectionListRef.value?.scrollToActiveItem()
}

function resetSearch() {
  searchQuery.value = ''
}

function handleClickClear() {
  resetSearch()
  scrollToActiveItem()
}

function isDefaultPlaceItem(place?: AnalysesPlaceItem) {
  return !place || place.translit === defaultPlace.translit
}

function onPlaceClick(selectedItem: CoreSelectionListSelectedItem) {
  const place = selectedItem as AnalysesPlaceItem

  draftPlace.value = isDefaultPlaceItem(place) ? undefined : place
}

function resetPlace() {
  draftPlace.value = undefined
  resetSearch()
  scrollToActiveItem()
}

function applyPlace() {
  emit('apply', draftPlace.value)
  emit('input', false)
}
</script>

<style lang="scss" scoped>
.analyses-lpu-place-drawer {
  &__footer {
    width: 100%;
  }
}
</style>
